const _excluded = ["children", "className", "direction", "maxSize", "minSize", "hideScrollbar", "gap", "itemMinSize", "itemMaxSize", "itemsInView", "style"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useMemo } from 'react';
import cn from 'classnames';
import styles from "./ScrollableList.module.scss";
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A scrollable container component that renders a list of items.
 * Supports both vertical and horizontal scrolling directions.
 */
export const ScrollableList = /*#__PURE__*/React.forwardRef(function ScrollableList(props, ref) {
  const {
      children,
      className,
      direction = 'horizontal',
      maxSize,
      minSize,
      hideScrollbar = false,
      gap,
      itemMinSize,
      itemMaxSize,
      itemsInView,
      style
    } = props,
    rest = _objectWithoutProperties(props, _excluded);
  const computedStyle = useMemo(() => {
    const styleObj = _objectSpread({}, style);

    // Helper to parse size values
    const formatToPx = size => "".concat(size, "px");
    const isPxValue = size => {
      if (!size) return false;
      return typeof size === 'number' || size.toString().endsWith('px');
    };
    const parseSize = size => {
      if (!size) return 0;
      return typeof size === 'number' ? size : parseFloat(size) || 0;
    };
    const sizeProperty = direction === 'vertical' ? 'height' : 'width';
    const maxSizeProperty = direction === 'vertical' ? 'maxHeight' : 'maxWidth';
    const minSizeProperty = direction === 'vertical' ? 'minHeight' : 'minWidth';

    // Set container min/max size constraints
    if (maxSize) {
      styleObj[maxSizeProperty] = maxSize;
    }
    if (minSize) {
      styleObj[minSizeProperty] = minSize;
    }

    // Set item min/max size constraints
    if (itemMinSize !== undefined) {
      styleObj['--item-min-size'] = formatToPx(itemMinSize);
    }
    if (itemMaxSize !== undefined) {
      styleObj['--item-max-size'] = formatToPx(itemMaxSize);
    }

    // Set gap
    if (gap) {
      styleObj.gap = formatToPx(gap);
    }

    // Calculate item size based on itemsInView if provided
    if (itemsInView) {
      const gapsCount = Math.ceil(itemsInView) - 1;
      const gapsTotal = Math.max(0, gapsCount) * (gap !== null && gap !== void 0 ? gap : 0);
      if (maxSize && isPxValue(maxSize)) {
        // Case: maxSize is provided
        const containerMinSize = isPxValue(minSize) ? parseSize(minSize) : 0;
        const containerMaxSize = Math.max(containerMinSize, parseSize(maxSize));
        const availableSpace = containerMaxSize - gapsTotal;
        const requiredItemSize = availableSpace / itemsInView;

        // Clamp to item constraints
        let finalItemSize = Math.floor(requiredItemSize);
        if (itemMinSize && finalItemSize < itemMinSize) {
          finalItemSize = itemMinSize;
        }
        if (itemMaxSize && finalItemSize > itemMaxSize) {
          finalItemSize = itemMaxSize;
        }
        styleObj[sizeProperty] = 'fit-content';
        styleObj['--item-size'] = formatToPx(finalItemSize);
      } else {
        if (itemMaxSize && itemMaxSize > 0) {
          // Case: maxItemSize is provided (no maxSize)
          const calculatedContainerSize = itemMaxSize * itemsInView + gapsTotal;
          styleObj[sizeProperty] = formatToPx(calculatedContainerSize);
          styleObj['--item-size'] = formatToPx(itemMaxSize);
        } else {
          // Case: no maxSize and no maxItemSize - use percentage
          const itemSizePercent = gapsTotal > 0 ? "calc((100% - ".concat(gapsTotal, "px) / ").concat(itemsInView, ")") : "calc(100% / ".concat(itemsInView, ")");
          styleObj['--item-size'] = itemSizePercent;
        }
      }
    }
    return styleObj;
  }, [direction, maxSize, minSize, gap, itemMinSize, itemMaxSize, itemsInView, style]);
  return /*#__PURE__*/_jsx("div", _objectSpread(_objectSpread({
    ref: ref,
    className: cn(styles.root, styles[direction], {
      [styles.hideScrollbar]: hideScrollbar
    }, className),
    style: computedStyle
  }, rest), {}, {
    children: children
  }));
});